import { Dropdown, Form, Input, Table } from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { errorCodeOptions, NewTimeSheetEntry } from './types';
import { useCurrentDateTime } from './hooks/useFormData';

interface TableColumn {
  key: string;
  header: string;
  width?: string;
  render: (formData: any, handlers: any, options?: any) => React.ReactNode;
  disabled?: (formData: any) => boolean;
}

interface TableColumnsProps {
  formData: NewTimeSheetEntry;
  handlers: any;
  options?: any;
}

const toValidDate = (value: any): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const TableColumnsComponent = ({
  formData,
  handlers,
  options,
}: TableColumnsProps) => {
  
  const { currentDateTime } = useCurrentDateTime();

  const isActionStarted = formData.actions === '1' || formData.actions === '4' ;
  const IsScopeOfWorksIncludesQc = formData.scope_of_Work.toLowerCase().includes("qc");


  const tableColumns: TableColumn[] = [
    {
      key: 'empId',
      header: 'Employee ID',
      render: (_, __, opts) => opts?.empId || '',
    },
    {
      key: "Jumbo Reel",
      header: "Jumbo Reel",
      render: (data, h) => (
    <Form.Dropdown
        placeholder="Jumbo Reel"
        selection
        options={[
          { key: 'Yes', value: "Yes", text: 'Yes' },
          { key: 'No', value: "No", text: 'No' },
        ]}
        value={data.jumbo_Reel}
        onChange={(e, { value }) => h.updateFormField('jumbo_Reel', value)}
        disabled={!h.computedValues?.isProjectSelected || isActionStarted}
        clearable
        style={{ minWidth: "100%", display: "block" }}
      />
      )
    },
    {
      key: 'startReel',
      header: 'Start Reel',
      width: '65px',
      render: (data, h) => (
        <Input
          value={data.start_Reel || ''}
          onChange={(e) => h.handleNumberInput('start_Reel', e.target.value)}
          placeholder="Enter Start Reel"
          disabled={!h.computedValues?.isProjectSelected || isActionStarted}
          style={{ width: '65px' }}
        />
      ),
    },
    {
      key: 'endReel',
      header: 'End Reel',
      width: '65px',
      render: (data, h) => (
        <Input
          value={data.end_Reel || ''}
          onChange={(e) => h.handleNumberInput('end_Reel', e.target.value)}
          placeholder="Enter End Reel"
          disabled={!h.computedValues?.isProjectSelected}
          style={{ width: '65px' }}
        />
      ),
    },
    {
      key: 'compReel',
      header: 'Comp Reel',
      width: '65px',
      render: (data) => (
        <Input
          value={data.comp_Reel || ''}
          readOnly
          style={{ width: '65px' }}
          disabled={isActionStarted}
        />
      ),
    },
    {
  key: 'startTime',
  header: 'Start Time',
  width: '300px',
  render: (data, h) => {
    const startTime = toValidDate(data.start_Time);
    return (
      <div style={{ width: '300px' }}>
        <DatePicker
          selected={data.actions === '' ? currentDateTime : startTime}
          onChange={() => {}} // Read-only
          showTimeSelect
          timeIntervals={5}
          dateFormat="dd-MM-yyyy HH:mm"
          customInput={<Input />}
          readOnly
          disabled={isActionStarted}
        />
      </div>
    );
  },
},
    {
  key: 'endTime',
  header: 'End Time',
  width: '300px',
  render: (data, h) => {
    const endTime = toValidDate(data.end_Time);
    // Don't show end time for active entries (action '1' or '4')

    return (
      <div style={{ width: '300px' }}>
        <DatePicker
          selected={data.actions === "" ? currentDateTime : endTime}
          onChange={() => {}}
          showTimeSelect
          timeIntervals={5}
          dateFormat="dd-MM-yyyy HH:mm"
          customInput={<Input />}
          readOnly
          disabled={isActionStarted}
        />
      </div>
    );
  },
},
    {
      key: 'duration',
      header: 'Duration',
      width: '75px',
      render: (data) => (
        <Input
          value={data.duration || ''}
          readOnly
          style={{ width: '75px' }}
          disabled={isActionStarted}
        />
      ),
    },
    {
      key: 'taskList',
      header: 'Task List',
      width: '175px',
      render: (data, __, opts) => (
        <Dropdown
          placeholder="Select Task List"
          fluid
          search
          selection
          clearable
          options={opts?.taskList?.options || []}
          value={data.task || ''}
          onChange={(e, { value }) =>
            handlers.handleTaskSelection(value as string)
          }
          disabled={!handlers.computedValues?.isProjectSelected || isActionStarted}
          style={{ width: '175px' }}
        />
      ),
    },
    {
      key: 'scopeOfWork',
      header: 'Scope of Work',
      width: '175px',
      render: (data, h, opts) => (
        <Dropdown
          placeholder="Select Scope of Work"
          fluid
          selection
          search
          clearable
          options={h.getScopeOptions(data.task || "") || []}
          value={data.scope_of_Work || ''}
          onChange={(e, { value }) =>
            h.updateFormField('scope_of_Work', value as string)
          }
          disabled={
            !handlers.computedValues?.isProjectSelected ||
            !data.task || 
            isActionStarted
          }
          style={{ width: '175px' }}
        />
      ),
    },
    {
      key: 'dpxSourceIn',
      header: 'DPX Source In',
      width: '100px',
      render: (data, h) => (
        <Input
          value={data.dpX_Source_In || ''}
          onChange={(e) => h.updateFormField('dpX_Source_In', e.target.value)}
          style={{ width: '100px' }}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'dpxSourceOut',
      header: 'DPX Source Out',
      width: '100px',
      render: (data, h) => (
        <Input
          value={data.dpX_Source_Out || ''}
          onChange={(e) => h.updateFormField('dpX_Source_Out', e.target.value)}
          style={{ width: '100px' }}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'errorDuration',
      header: 'Error Duration',
      width: '100px',
      render: (data, h) => (
        <Input
          value={data.error_Duration || ''}
          onChange={(e) => h.updateFormField('error_Duration', e.target.value)}
          style={{ width: '100px' }}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'movRecordIn',
      header: 'MOV Record In',
      width: '100px',
      render: (data, h) => (
        <Input
          value={data.moV_Record_In || ''}
          onChange={(e) => h.updateFormField('moV_Record_In', e.target.value)}
          style={{ width: '100px' }}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'movRecordOut',
      header: 'MOV Record Out',
      width: '100px',
      render: (data, h) => (
        <Input
          value={data.moV_Record_Out || ''}
          onChange={(e) => h.updateFormField('moV_Record_Out', e.target.value)}
          style={{ width: '100px' }}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'noOfCorrections',
      header: 'No. of Corrections',
      width: '65px',
      render: (data, h) => (
        <Input
          value={data.no_of_Corrections || ''}
          onChange={(e) =>
            h.handleNumberInput('no_of_Corrections', e.target.value)
          }
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
          style={{ width: '65px' }}
        />
      ),
    },
    {
      key: 'typesOfCorrection',
      header: 'Types of Correction',
      width: '150px',
      render: (data, h) => (
        <Input
          value={
            data.types_of_Correction || ''
          }
          onChange={(e) =>
            h.updateFormField('types_of_Correction', e.target.value)
          }
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
          style={{ width: '150px' }}
        />
      ),
    },
    {
      key: 'errorCode',
      header: 'Error Code',
      width: '100px',
      render: (data, h) => (
        <Dropdown
          placeholder="Select Error Code"
          fluid
          selection
          search
          clearable
          value={data.error_Code || ''}
          onChange={(e, { value }) =>
            h.updateFormField('error_Code', value as string)
          }
          style={{ width: '100px' }}
          options={errorCodeOptions}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'errorRemarks',
      header: 'Error Remarks',
      width: '150px',
      render: (data, h) => (
        <Input
          value={data.error_Remarks || ''}
          onChange={(e) => h.updateFormField('error_Remarks', e.target.value)}
          style={{ width: '150px' }}
          disabled={!h.computedValues?.isProjectSelected || isActionStarted || !IsScopeOfWorksIncludesQc}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (data, h, opts) => (
        <Dropdown
          placeholder="Select Status"
          fluid
          selection
          search
          clearable
          options={opts?.statusOptions || []}
          value={data.status || ''}
          onChange={(e, { value }) =>
            h.updateFormField('status', value as string)
          }
          disabled={!h.computedValues?.isProjectSelected || isActionStarted}
        />
      ),
    },
    {
      key: 'remarks',
      header: 'Remarks',
      width: '350px',
      render: (data, h) => (
        <Input
          value={data.remarks || ''}
          onChange={(e) => h.updateFormField('remarks', e.target.value)}
          placeholder="Enter remarks"
          disabled={!h.computedValues?.isProjectSelected}
          style={{ width: '350px' }}
        />
      ),
    },
  ];
  return (
    <>
      {tableColumns.map((column) => (
        <Table.Cell key={column.key} style={{ width: column.width }}>
          {column.render(formData, handlers, options)}
        </Table.Cell>
      ))}
    </>
  );
};

export default TableColumnsComponent;
